import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { PeriodItemEntry } from 'api/queries/statusChangesTableData.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { useStatusChangesLogic } from 'components/widgets/TopStatusChanges/StatusChanges.logic';
import { useStatusChangesProvider } from 'components/widgets/TopStatusChanges/StatusChanges.provider';
import { LocalFilters } from 'components/widgets/TopStatusChanges/localFilters/LocalFilters';
import { columnsV8, initialState } from 'components/widgets/TopStatusChanges/statusChanges.utils';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

const { componentKey, id, elementId, endpoint } = widgetsConfigurations.topStatusChanges;

export const StatusChanges = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    isSinglePage = false,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.topStatusChanges.title,
    setIsPayloadReady,
}: ITableWidgetWrapperProps) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    const { ui, api } = useStatusChangesProvider({
        widgetId,
        globalFilterData,
        localFilters,
        contractId,
        latestVersionId,
        projectId,
        compareVersionId,
        table,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getStatusChangesTableData.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useStatusChangesLogic({
        ui,
        api,
        table,
        title,
        subTitle,
        isSinglePage,
        setIsPayloadReady,
        widgetViewMode,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    !WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode) && (
                        <LocalFilters
                            key={'localFilters'}
                            status={ui.state.status}
                            setStatus={ui.setters.setStatus}
                            isCriticalPath={ui.state.isCriticalPath}
                            setIsCriticalPath={ui.setters.setIsCriticalPath}
                        />
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        elementId={elementId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={true}
                        shouldUseNewIcon
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        apiPayload={api.getStatusChangesTableData.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'export'}
                            title={title}
                            subTitle={subTitle}
                            handleAPIExcelExport={logic.handleAPIExcelExport}
                        />
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            onChangeOrder={table.setColumnOrder}
                            onChangeVisibility={table.setColumnVisibility}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getStatusChangesTableData.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
            >
                {logic.filteredData && (
                    <TableContainer className={classes.tableWrapperContainer}>
                        {logic.tabsArrData.length > 0 && (
                            <Tabs
                                tabs={logic.tabsArrData}
                                value={ui.state.activeTab}
                                onChange={(value) => ui.setters.setActiveTab(value as TKeys)}
                            />
                        )}

                        {table.updatedInitialState && (
                            <TableV8<PeriodItemEntry>
                                data={logic.filteredData}
                                columns={columnsV8}
                                isLoading={
                                    api.getStatusChangesTableData.instance.isLoading ||
                                    api.getStatusChangesTableData.instance.isFetching
                                }
                                noData={<NoData>No Changes To Display</NoData>}
                                sorting={table.sorting}
                                setSorting={table.setSorting}
                                columnVisibility={table.columnVisibility}
                                setColumnVisibility={table.setColumnVisibility}
                                columnOrder={table.columnOrder}
                                setColumnOrder={table.setColumnOrder}
                                manualPagination={false}
                                manualSorting={false}
                                pagination={table.pagination}
                                rowSelection={table.rowSelection}
                                setRowSelection={table.setRowSelection}
                                tableClassName={
                                    WidgetViewModeUtils.isReportView(widgetViewMode)
                                        ? classesGeneralWidget.reportTableScrollable
                                        : classesGeneralWidget.pageTableScrollable
                                }
                                wrapperClassName={
                                    WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
                                        ? classesGeneralWidget.reportTableWrapper
                                        : ''
                                }
                            />
                        )}

                        {logic.filteredData.length > smallPageSize &&
                            !isSinglePage &&
                            !WidgetViewModeUtils.isInAnyReportMode(widgetViewMode) && (
                                <TableFooter
                                    setPageSize={logic.setPageSize}
                                    pageSize={logic.pageSize}
                                    link={!widgetId ? executionConfig.statusChanges.link : undefined}
                                />
                            )}
                    </TableContainer>
                )}
                {logic.spinnerOpen && <OverlayWithSpinner />}
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
